if (!customElements.get('page-ranking-swiper')) {
  class PageRankingSwiper extends HTMLElement {
    connectedCallback() {
      this.container = this.querySelector('.page-ranking-swiper__swiper');
      if (!this.container) return;

      this.slideCount = this.querySelectorAll('.swiper-slide').length;
      this.prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      this.resizeObserver = null;
      this.viewportObserver = null;
      this.initialized = false;

      this.whenSwiperReady(() => this.setup());
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.viewportObserver?.disconnect();
      this.viewportObserver = null;

      if (this.onResize) {
        window.removeEventListener('resize', this.onResize);
        this.onResize = null;
      }

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    whenSwiperReady(callback) {
      if (typeof Swiper !== 'undefined') {
        callback();
        return;
      }

      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (typeof Swiper !== 'undefined') {
          window.clearInterval(timer);
          callback();
          return;
        }

        if (Date.now() - startedAt > 8000) {
          window.clearInterval(timer);
          console.error('Swiper is not defined.');
        }
      }, 50);
    }

    setup() {
      if (this.initialized || !this.isConnected) return;
      this.initialized = true;
      this.initSwiper();
      this.observeResize();
      this.initViewportAutoplay();
    }

    isAutoplayEnabled() {
      return (
        this.dataset.autoplay === 'true' &&
        !this.prefersReducedMotion &&
        this.slideCount > 1
      );
    }

    getPxUnit() {
      const host = this.closest('.page-ranking-swiper') || this;
      const value = getComputedStyle(host).getPropertyValue('--ranking-px').trim();
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    getGaps() {
      const px = this.getPxUnit();
      return {
        mobile: Math.round(13 * px),
        desktop: Math.round(100 * px),
      };
    }

    initSwiper() {
      const gaps = this.getGaps();
      const enableLoop = this.slideCount > 1;
      const enableAutoplay = this.isAutoplayEnabled();
      const autoplayDelay = Number(this.dataset.autoplayDelay) || 3000;

      this.swiper = new Swiper(this.container, {
        slidesPerView: 2,
        spaceBetween: gaps.mobile,
        loop: enableLoop,
        loopedSlides: this.slideCount,
        loopAdditionalSlides: 2,
        watchOverflow: !enableLoop,
        grabCursor: true,
        simulateTouch: true,
        speed: this.prefersReducedMotion ? 0 : 600,
        a11y: true,
        autoplay: enableAutoplay
          ? {
              delay: autoplayDelay,
              disableOnInteraction: false,
              waitForTransition: true,
              stopOnLastSlide: false,
            }
          : false,
        breakpoints: {
          851: {
            slidesPerView: 3,
            spaceBetween: gaps.desktop,
          },
        },
      });

      // Start only when in viewport.
      if (enableAutoplay && this.swiper.autoplay) {
        this.swiper.autoplay.stop();
      }
    }

    initViewportAutoplay() {
      if (!this.isAutoplayEnabled() || !this.swiper?.autoplay) return;

      this.viewportObserver = new IntersectionObserver(
        ([entry]) => {
          if (!this.swiper?.autoplay) return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
            this.swiper.autoplay.start();
          } else {
            this.swiper.autoplay.stop();
          }
        },
        {
          threshold: [0, 0.25, 0.5, 0.75, 1],
          rootMargin: '0px 0px -8% 0px',
        }
      );

      this.viewportObserver.observe(this);
    }

    observeResize() {
      if (typeof ResizeObserver === 'undefined') {
        this.onResize = () => this.updateSpacing();
        window.addEventListener('resize', this.onResize, { passive: true });
        return;
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.updateSpacing();
      });
      this.resizeObserver.observe(this.closest('.page-ranking-swiper') || this);
    }

    updateSpacing() {
      if (!this.swiper) return;

      const gaps = this.getGaps();
      const isMobile = window.matchMedia('(max-width: 850px)').matches;

      this.swiper.params.spaceBetween = isMobile ? gaps.mobile : gaps.desktop;
      this.swiper.params.slidesPerView = isMobile ? 2 : 3;

      if (this.swiper.params.breakpoints && this.swiper.params.breakpoints[851]) {
        this.swiper.params.breakpoints[851].spaceBetween = gaps.desktop;
        this.swiper.params.breakpoints[851].slidesPerView = 3;
      }

      this.swiper.update();
    }
  }

  customElements.define('page-ranking-swiper', PageRankingSwiper);
}
